import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getPublicDonors } from '@/services/donors.api';
import { FrontendDonor } from '@/services/transform';

export default function PartnersWall() {
  const { t, i18n } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [partners, setPartners] = useState<FrontendDonor[]>([]);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const loadPartners = async () => {
      try {
        const data = await getPublicDonors();
        // Filter only partners (type === 'partner')
        const partnerList = data.filter(d => d.type === 'partner');
        setPartners(partnerList);
      } catch (error) {
        console.error('加载合作伙伴失败:', error);
        setPartners([]);
      }
    };
    loadPartners();
  }, []);

  const isZh = i18n.language === 'zh';
  
  // If no partners, hide the section completely
  if (partners.length === 0) {
    return null;
  }

  return (
    <section className="py-20 md:py-24 bg-[#F7F9FC]" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className={`text-center mb-12 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#2C7DA0]/10 text-[#2C7DA0] text-xs font-semibold mb-4 tracking-widest uppercase">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-links-line" />
            </div>
            {t('partners.home.badge')}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#0A2540] mb-3">
            {t('partners.home.title')}
          </h2>
          <p className="text-[#0A2540]/50 text-base max-w-xl mx-auto">
            {t('partners.home.subtitle')}
          </p>
        </div>

        {/* Partner Grid */}
        <div className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7 gap-4 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          {partners.map((partner) => (
            <Link
              to="/partners"
              key={partner.id}
              className="group relative flex flex-col items-center justify-center bg-white rounded-xl p-4 h-24 cursor-pointer transition-all duration-200 hover:-translate-y-1"
            >
              <div
                className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-sm shrink-0 mb-2"
                style={{ backgroundColor: partner.color }}
              >
                {partner.abbr.length > 4 ? partner.abbr.slice(0, 4) : partner.abbr}
              </div>
              <span className="text-[10px] text-[#0A2540]/50 text-center leading-tight line-clamp-2">
                {isZh ? partner.name : partner.nameEn}
              </span>
              {/* Hover Tooltip */}
              <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-[#0A2540] text-white text-xs px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                {isZh ? partner.name : partner.nameEn}
              </div>
            </Link>
          ))}
        </div>

        {/* View All */}
        <div className={`text-center mt-10 transition-all duration-700 delay-300 ${visible ? 'opacity-100' : 'opacity-0'}`}>
          <Link
            to="/partners"
            className="inline-flex items-center gap-2 px-6 py-3 border-2 border-[#0A2540]/20 hover:border-[#2C7DA0] text-[#0A2540] hover:text-[#2C7DA0] rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            {t('partners.home.viewAll')}
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line" />
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}
